import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { RootState, AppDispatch } from '../../app/store'
import { setTempEmployeeId } from '../../features/auth/authSlice'
import toast from 'react-hot-toast'
import {
  LiftPage,
  NumericKeyboard,
  FlutterButton,
  InputBox
} from '../../components/auth'

const LoginPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  
  const { isLoading } = useSelector((state: RootState) => state.auth)
  const { settings } = useSelector((state: RootState) => state.settings)
  
  const [employeeId, setEmployeeId] = useState('')

  const isRTL = settings.language === 'ar'

  // Initialize employee ID from storage or URL - matches Flutter logic
  useEffect(() => {
    // Prevent scrolling
    document.body.classList.add('no-scroll')
    
    // Check for stored employee ID first
    const storedEmployeeId = localStorage.getItem('temp_employee_id')
    if (storedEmployeeId) {
      setEmployeeId(storedEmployeeId)
      return
    }

    // Check URL parameters for employee ID - matches Flutter's URL handling
    const urlParams = new URLSearchParams(window.location.search)
    const urlEmployeeId = urlParams.get('employee_id')
    if (urlEmployeeId) {
      setEmployeeId(urlEmployeeId)
      dispatch(setTempEmployeeId(urlEmployeeId))
      return
    }

    // Check hash fragments for employee ID - matches Flutter's hash handling
    const hash = window.location.hash
    if (hash) {
      try {
        const hashParams = new URLSearchParams(hash.substring(1))
        const hashEmployeeId = hashParams.get('employee_id')
        if (hashEmployeeId) {
          setEmployeeId(hashEmployeeId)
          dispatch(setTempEmployeeId(hashEmployeeId))
          return
        }
      } catch (e) {
        // Ignore hash parsing errors
      }
    }
    
    // Cleanup function
    return () => {
      document.body.classList.remove('no-scroll')
    }
  }, [dispatch])

  const handleSubmit = async () => {
    if (employeeId.length !== 5) {
      toast.error(t('validation.employee_id_length'))
      return
    }

    try {
      // Store employee ID temporarily - matches Flutter logic
      dispatch(setTempEmployeeId(employeeId))
      
      // Navigate to PIN page with employee ID - matches Flutter navigation
      navigate('/login-pin', { 
        state: { employeeId },
        replace: true 
      })
    } catch (error: any) {
      toast.error(error.message || t('auth.login_failed'))
    }
  }

  const addNumber = (number: string) => {
    if (employeeId.length < 5) {
      setEmployeeId(prev => prev + number)
    }
  }

  const deleteNumber = () => {
    setEmployeeId(prev => prev.slice(0, -1))
  }

  return (
    <div className="h-screen bg-[#F5F5F5] flex overflow-hidden">
      {/* Main content area */}
      <div className="flex-1 flex flex-col items-center justify-center" style={{ direction: 'ltr' }}>
        {/* Title */}
        <h1 
          className="text-black font-medium mb-[clamp(12px,1.5vw,16px)] text-[clamp(20px,2.2vw,32px)] font-['IBM Plex Sans Arabic']"
          style={{ textAlign: isRTL ? 'right' : 'left' }}
        >
          {t('auth.enter_employee_id')}
        </h1>
        
        {/* Container for consistent width */}
        <div className="w-full max-w-[clamp(320px,65vw,580px)] flex flex-col items-center px-[clamp(12px,1.5vw,16px)]">
          {/* Input boxes */}
          <div className="flex justify-center mb-[clamp(8px,1vw,12px)] w-full">
            {[0, 1, 2, 3, 4].map(index => (
              <InputBox key={index} value={employeeId[index] || ''} />
            ))}
          </div>
          
          {/* Spacing */}
          <div className="h-[clamp(4px,0.5vw,8px)]"></div>
          
          {/* Keyboard */}
          <div className="w-full mb-[clamp(12px,1.5vw,16px)]">
            <NumericKeyboard 
              onNumberPress={addNumber}
              onDeletePress={deleteNumber}
              disabled={isLoading}
            />
          </div>
          
          {/* Spacing */}
          <div className="h-[clamp(8px,1vw,16px)]"></div>
          
          {/* Button - aligned with keyboard width */}
          <div className="w-full">
            <FlutterButton
              text={t('common.next')}
              onPress={handleSubmit}
              disabled={employeeId.length !== 5}
              isLoading={isLoading}
              loadingText={t('common.loading')}
            />
          </div>
        </div>
      </div>   
      {/* Lift page */}
      <LiftPage />
    </div>
  )
}

export default LoginPage
